import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Database, ListChecks, LogOut, Radar, Search, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Nouvelle Recherche", icon: Search, end: true, id: "nav-new-search" },
  { to: "/taches", label: "Gestionnaire de tâches", icon: ListChecks, id: "nav-task-queue", badge: "jobs_active" },
  { to: "/leads", label: "Base de données Leads", icon: Database, id: "nav-leads", badge: "leads_total" },
  { to: "/parametres", label: "Paramètres & Intégrations", icon: Settings2, id: "nav-settings" },
];

const NavItems = ({ stats, compact }) =>
  NAV.map((item, i) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      data-testid={compact ? `${item.id}-mobile` : item.id}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
          isActive ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
        } ${compact ? "shrink-0" : ""}`
      }
    >
      {!compact && <span className="font-mono text-[10px] opacity-60">0{i + 1}</span>}
      <item.icon className="h-4 w-4" />
      <span className={compact ? "hidden sm:inline" : ""}>{item.label}</span>
      {item.badge && stats?.[item.badge] > 0 && !compact && (
        <span className="ml-auto rounded bg-zinc-950/10 px-1.5 font-mono text-[10px] group-[.active]:bg-white/20">
          {stats[item.badge]}
        </span>
      )}
    </NavLink>
  ));

export const AppShell = () => {
  const { user, logout } = useAuth();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats").then((r) => r.data), refetchInterval: 5000 });

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col border-r border-border bg-white md:flex">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#0A0A0A] text-white">
            <Radar className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-black leading-none tracking-tight">AYA<span className="text-[#FF3B30]">.Lead</span></div>
            <div className="eyebrow mt-1 !text-[9px]">B2B Multi-Sources</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-3">
          <NavItems stats={stats} />
        </nav>
        <div className="border-t border-border p-4">
          <div className="eyebrow mb-1">Connecté</div>
          <div data-testid="current-user-email" className="truncate text-sm font-medium">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-3 flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </aside>
      <header className="sticky top-0 z-30 flex items-center gap-2 overflow-x-auto border-b border-border bg-white px-3 py-2 md:hidden">
        <Radar className="h-5 w-5 shrink-0" />
        <NavItems stats={stats} compact />
        <button data-testid="logout-button-mobile" onClick={logout} className="ml-auto shrink-0 p-2 text-zinc-500">
          <LogOut className="h-4 w-4" />
        </button>
      </header>
      <main className="min-h-screen p-4 sm:p-6 md:ml-64 md:p-10">
        <Outlet />
      </main>
    </div>
  );
};
